/**
 * Laid out as the template's checkout, COPIED from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/checkout/index.tsx
 *   the 8/4 grid: the form's sections on the left, a sticky summary on the right;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/checkout/shipping-method.tsx
 *   the bordered section card with a body under a rule, and the `AdvancedRadio` choice cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/checkout/order-summery.tsx
 *   the summary's rows, the total under a rule and the full-width submit.
 * vendor/isomorphic/apps/isomorphic/src/app/shared/modal-views/container.tsx
 *   the rizzui `Modal` and its dark-mode overlay, for the progress dialog.
 * The hero is the order page's own `PageHero`; the upload box is the brand form's `Upload`.
 *
 * A Super Admin's "complete these steps now", offered where a forced move past Packed is refused:
 * one numbered card per step the order still has to take, and on submit `completeSteps` takes them
 * through each step's own endpoint. Everything is asked for up front; the run never dispatches
 * before the invoice is finalised — the progress dialog waits for Billing, and stops there (keeping
 * what was typed) if the invoice does not come. Save packing only stops at the invoice. Warehouse judges every reading — a reason or an override field
 * appears only once Warehouse has said a reading is amber or red. A refused step stops the run, the
 * steps before it stay taken, and the form keeps what was typed; submitting again carries on.
 */
import { useEffect, useRef, useState, type FormEvent, type PointerEvent, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, Navigate, useLoaderData, useNavigate, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowRightBold as ArrowRight,
  PiCheckBold as Check,
  PiCheckCircleFill as Chosen,
  PiPackageDuotone as PackIcon,
  PiReceiptDuotone as InvoiceIcon,
  PiScalesDuotone as ShortIcon,
  PiSignatureDuotone as SignatureIcon,
  PiTruckDuotone as TruckIcon,
  PiVanDuotone as VanIcon,
  PiWarningBold as Warning,
} from "react-icons/pi";
import { AdvancedRadio, Alert, Avatar, Badge, Button, Checkbox, Loader, Text, Title } from "rizzui";
import { CompleteProgress, type StepStatus } from "../../components/orders/complete-progress";
import Upload from "@core/ui/upload";
import { ErrorSurface, Field, cn, problemTitle } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { PageHero, StatusPill } from "../../components/detail-parts";
import { UploadDropHint } from "../../components/upload-drop-hint";
import { canCompleteFrom, completeSteps, isCompleteTarget, packedAsOrdered, type CompleteInput } from "../../lib/complete-order";
import { getDispatchView, money, orderByKey, packRecorded, reached, stateLabel, stepsBefore, type DeliveryType } from "../../lib/services/ordering";
import { listDispatchers } from "../../lib/services/identity";
import { packingTaskForOrder, reasonCodeLabel, type Band, type ReasonCode } from "../../lib/services/warehouse";
import { TRANSPORT_MODES } from "../../lib/services/dispatch";
import { formatMoney } from "../../lib/format";
import { can } from "../../lib/permissions";
import { orderPath } from "../../lib/paths";
import { useGoBack } from "../../lib/navigation";
import type { AccountSummary, PermissionState } from "../../types";

export async function completeStepsLoader({ params, request }: LoaderFunctionArgs) {
  const order = await orderByKey(request, params.orderNumber!);
  // undefined when Warehouse could not be read; the run then reports its own failure.
  const task = packRecorded(order) ? null : await packingTaskForOrder(order.id).catch(() => undefined);
  // null when unreadable: the picker then says so, and the pincodes are simply not filled in.
  const [dispatchers, dispatch] = await Promise.all([listDispatchers().catch(() => null), getDispatchView(order.id).catch(() => null)]);
  return { order, task, dispatchers, dispatch };
}

const REASON_CODES: readonly NonNullable<ReasonCode>[] = [
  "rolling_tolerance",
  "short_length_offcut",
  "packing_material_included",
  "partial_quantity_balance_to_follow",
  "scale_error",
  "wrong_product_repick",
  "other",
];

/** Warehouse's refusal codes for one reading, in words — and which band each one means it judged. */
const REFUSAL: Record<string, { text: string; band?: Band }> = {
  weight_required: { text: "Enter the packed weight." },
  quantity_required: { text: "Enter the packed quantity." },
  piece_count_required: { text: "Enter the number of pieces." },
  dimension_required: { text: "Enter the cut length, or tick Standard size." },
  cross_check_weight_required: { text: "Warehouse also needs the total weight of these pieces." },
  reason_code_required: { text: "Outside tolerance (amber). Choose a reason and submit again to save it.", band: "amber" },
  reason_note_required: { text: "Say what the other reason is.", band: "amber" },
  supervisor_override_required: { text: "Far outside tolerance (red). It saves only with your override and a reason.", band: "red" },
  override_reason_required: { text: "Write the reason for the override.", band: "red" },
};

/** Billing's transport record, asked for In-House only — a third-party handover asks for nothing. `always`: Billing needs it on every record. */
const TRANSPORT: readonly { name: string; label: string; type?: string; always?: boolean }[] = [
  { name: "transporterName", label: "Transporter name", always: true },
  { name: "transporterId", label: "Transporter ID (GSTIN)", always: true },
  { name: "vehicleNumber", label: "Vehicle number", always: true },
  { name: "distanceKm", label: "Distance (km)", type: "number", always: true },
  { name: "docketNumber", label: "Docket / LR number" },
  { name: "contactPhone", label: "Contact phone", type: "tel" },
];

const METHODS: readonly { value: DeliveryType; title: string; text: string; icon: ReactNode }[] = [
  { value: "in_house", title: "In-house", text: "Our own vehicle takes it out now; the delivery is recorded with a signature.", icon: <VanIcon aria-hidden className="size-6" /> },
  { value: "third_party", title: "Third-party transporter", text: "Handed over to the transporter — nothing to fill in; the transporter's delivery is confirmed.", icon: <TruckIcon aria-hidden className="size-6" /> },
];

/** What each card says while it is the step being taken. */
const RUNNING: Record<string, string> = {
  approved: "Approving",
  packed: "Recording the pack",
  invoiced: "Finalising the invoice",
  dispatched: "Dispatching",
  delivered: "Recording the delivery",
};

/** What the progress dialog says while each step is being taken. */
const WAITING: Record<string, string> = {
  approved: "Approving the order…",
  packed: "Recording the pack with Warehouse. Stock is taken as it completes…",
  invoiced: "Waiting for Billing to raise the invoice from the pack, then finalising it. This usually takes a few seconds…",
  dispatched: "Dispatching…",
  delivered: "Recording the delivery…",
};


function readImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function CompleteStepsPage() {
  const { order, task, dispatchers, dispatch } = useLoaderData() as Awaited<ReturnType<typeof completeStepsLoader>>;
  const { account, permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const target = searchParams.get("to");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const goBack = useGoBack(orderPath(order.orderNumber));

  const packed = packRecorded(order);
  const facts = { packRecorded: packed, invoiced: order.invoiceId !== null };
  // Forced past Packed before the invoice existed: Billing's invoice reaches an order only at Packed
  // (Ordering refuses it anywhere else), so it has to be moved back before the steps can be taken.
  const stranded = !facts.invoiced && reached(order.state, "invoiced") && order.state !== "cancelled";
  const runnable = canCompleteFrom(order.state) && !stranded;
  const left = isCompleteTarget(target) ? stepsBefore(order.state, facts, target) : [];
  // The steps as they stood on arrival, so the tracker can tick them off as the run takes them.
  const [plan] = useState(left);
  const [from] = useState(order.state);
  const [running, setRunning] = useState<string | null>(null);
  const [stoppedAt, setStoppedAt] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  // Per order line: the band Warehouse last judged, and what it said.
  const [bands, setBands] = useState<Record<string, Band>>({});
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const [shortPack, setShortPack] = useState(false);
  const [invoiceLate, setInvoiceLate] = useState(false);
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(order.deliveryType === "third_party" ? "third_party" : "in_house");
  const [signature, setSignature] = useState<{ name: string; data: string } | null>(null);
  // The receiver signs on screen, or a photo of the signed invoice or challan is uploaded.
  const [signMode, setSignMode] = useState<"draw" | "upload">("draw");
  // Per order line: Standard size ticked, which stands in for a cut length.
  const [standard, setStandard] = useState<Record<string, boolean>>({});
  // Per order line not ordered in kg: the person said it was packed less than ordered, so it is asked for.
  const [packedLess, setPackedLess] = useState<Record<string, boolean>>({});
  // The progress dialog: open while a run works, and after Save packing until the person moves on to dispatch.
  // "stopped": the run waited for the invoice and it did not come.
  const [progress, setProgress] = useState<"closed" | "running" | "invoiced" | "stopped">("closed");
  // One id per line for the whole visit: a resent reading Warehouse already took is a duplicate, not a second entry.
  const [entryIds] = useState(() => new Map((task?.lines ?? []).map((line) => [line.orderLineId, crypto.randomUUID()])));

  if (!can(permissionState, "orders.override")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="complete this order's steps" />;
  }
  if (!isCompleteTarget(target) || left.length === 0) return <Navigate replace to={orderPath(order.orderNumber)} />;

  const live = new Set((task?.entries ?? []).filter((entry) => entry.isLive).map((entry) => entry.orderLineId));
  const invoiced = order.invoiceId !== null;
  const showDispatch = !["packed", "invoiced"].includes(target) && !["dispatched", "delivered"].includes(order.state);
  const showDelivery = target === "delivered";
  // Save packing only: a second submit button that stops the run at the invoice.
  const canStopAtInvoice = !invoiced && !["packed", "invoiced"].includes(target);
  const doneStep = (step: string) =>
    reached(order.state, step) && (packed || !reached(step, "packed")) && (facts.invoiced || !reached(step, "invoiced"));
  // The run reports each step as it starts, after the one before it finished — so a step before the
  // running one is done, though the order on this page is only re-read when the run ends. Picking /
  // Packing is the exception: the pack walks the order through it to Packed in one go.
  const partOfRunning = (step: string) => step === "picking_packing" && running === "packed";
  const ranPast = (step: string) => running !== null && !partOfRunning(step) && plan.indexOf(step) < plan.indexOf(running);
  const statusOf = (step: string): StepStatus =>
    running === step || (partOfRunning(step) && !doneStep(step))
      ? "running"
      : stoppedAt === step
        ? "stopped"
        : doneStep(step) || ranPast(step)
          ? "done"
          : "todo";
  const numberOf = (step: string) => plan.indexOf(step) + 1;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isCompleteTarget(target)) return;
    const runTo = (event.nativeEvent as SubmitEvent).submitter?.getAttribute("value") === "pack" ? "invoiced" : target;
    if (runTo === "delivered" && showDelivery && deliveryType === "in_house" && !signature) {
      toast.error("Add the receiver's signature before submitting");
      return;
    }
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const num = (name: string) => (text(name) === "" ? null : Number(text(name)));
    const input: CompleteInput = {
      target: runTo,
      actorId: account.id,
      readings: (task?.lines ?? []).map((line) => {
        const id = line.orderLineId;
        const asOrdered = packedLess[id] ? null : packedAsOrdered(line);
        return {
          orderLineId: id,
          clientEntryId: entryIds.get(id) ?? crypto.randomUUID(),
          ...(asOrdered ?? {
            actualWeight: num(`weight:${id}`),
            actualPieceCount: num(`pieces:${id}`),
            actualDimension: num(`length:${id}`),
            standardSizeConfirmed: form.has(`standard:${id}`),
          }),
          band: bands[id] ?? "green",
          reasonCode: (text(`reason:${id}`) || null) as ReasonCode,
          reasonNote: text(`note:${id}`) || null,
          overrideReason: text(`override:${id}`) || null,
        };
      }),
      shipShortReason: text("shipShortReason"),
      deliveryType,
      dispatcherId: text("dispatcherId") || null,
      transport: {
        transporterName: text("transporterName") || null,
        transporterId: text("transporterId") || null,
        vehicleNumber: text("vehicleNumber") || null,
        distanceKm: num("distanceKm"),
        modeOfTransport: text("modeOfTransport") || null,
        fromPincode: text("fromPincode") || null,
        toPincode: text("toPincode") || null,
        docketNumber: text("docketNumber") || null,
        contactPhone: text("contactPhone") || null,
      },
      receiverName: text("receiverName"),
      signature: signature?.data ?? "",
      shortDelivered: Object.fromEntries(
        order.lines
          .filter((line) => text(`delivered:${line.lineId}`) !== "")
          .map((line) => [line.lineId, { quantity: Number(text(`delivered:${line.lineId}`)), reason: text(`shortage:${line.lineId}`) }]),
      ),
    };

    setError(null);
    setStoppedAt(null);
    setProgress("running");
    let current: string | null = null;
    const step = (state: string) => {
      current = state;
      setRunning(state);
    };
    try {
      const result = await completeSteps(order.id, input, step);
      if (result.stop === "done" && runTo === target) {
        toast.success(`${order.orderNumber} is ${stateLabel(target)}`);
        navigate(`${orderPath(order.orderNumber)}?done=advanced`);
        return;
      }
      if (result.stop === "done") {
        setProgress("invoiced");
        return;
      }
      if (result.stop !== "invoice") setProgress("closed");
      if (result.stop === "pack") {
        const lineOf = new Map(input.readings.map((reading) => [reading.clientEntryId, reading.orderLineId]));
        const nextBands = { ...bands };
        const said: Record<string, string> = {};
        for (const rejected of result.outcome.rejected) {
          const line = lineOf.get(rejected.clientEntryId);
          if (!line) continue;
          const known = REFUSAL[rejected.reason];
          if (known?.band) nextBands[line] = known.band;
          said[line] = known?.text ?? reasonCodeLabel(rejected.reason);
        }
        for (const held of result.outcome.conflicts) {
          const line = lineOf.get(held.clientEntryId);
          if (line) said[line] = `Recorded, but held for a supervisor's decision (${reasonCodeLabel(held.kind)}). Decide it under Packing before the pack can complete.`;
        }
        setBands(nextBands);
        setRefusals(said);
        setStoppedAt("packed");
        toast.error("Warehouse did not accept every reading");
      } else if (result.stop === "short") {
        setShortPack(true);
        setStoppedAt("packed");
        toast.error("Warehouse packed less than was ordered");
      } else {
        setInvoiceLate(true);
        setStoppedAt("invoiced");
        setProgress("stopped");
      }
    } catch (err) {
      setError(err);
      setStoppedAt(current);
      setProgress("closed");
      toast.error(problemTitle(err, "Could not complete the steps"));
    } finally {
      setRunning(null);
      revalidator.revalidate();
    }
  }

  const busy = running !== null;

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[
          { name: "Home", href: "/" },
          { name: "Orders", href: "/orders" },
          { name: order.orderNumber, href: orderPath(order.orderNumber) },
          { name: "Complete steps" },
        ]}
        back={{ href: orderPath(order.orderNumber), label: "Back to order" }}
        icon={<TruckIcon aria-hidden />}
        title={`Take ${order.orderNumber} to ${stateLabel(target)}`}
        description="Every step goes through the same route as when it is done on its own: the pack takes stock, Billing raises the invoice, and the dispatch is recorded. Nothing is skipped."
        meta={
          <>
            <StatusPill status={order.state} />
            <ArrowRight aria-hidden className="size-4 text-gray-400" />
            <StatusPill status={target} />
          </>
        }
      />

      {stranded ? (
        <ErrorSurface
          error={{
            title: `${order.orderNumber} is ${stateLabel(order.state)} without a finalised invoice`,
            status: 409,
            detail: "It was forced past Packed before its invoice existed. Set it back to Packed (Move to → Packed on the order), then come back here: Billing's invoice reaches an order only at Packed.",
          }}
          action="complete these steps"
        />
      ) : null}
      {!canCompleteFrom(order.state) ? (
        <ErrorSurface
          error={{ title: `${order.orderNumber} is ${stateLabel(order.state)}`, status: 409, detail: "Its payment or approval has to come first, and that cannot be given here." }}
          action="complete these steps"
        />
      ) : null}
      {error ? <ErrorSurface error={error} action="complete these steps" /> : null}

      <form onSubmit={(event) => void submit(event)} className="items-start @5xl:grid @5xl:grid-cols-12 @5xl:gap-7 @7xl:gap-10">
        <fieldset disabled={busy} className="flex min-w-0 flex-col gap-6 @5xl:col-span-8">
          {!packed ? (
            <StepCard
              number={numberOf("packed")}
              status={statusOf("packed")}
              icon={<PackIcon aria-hidden className="size-5" />}
              title="Pack"
              description={`What was actually packed, line by line. Warehouse checks each reading against its tolerance, as it does from the app.${task?.assignedToName ? ` Completing it here takes it off ${task.assignedToName}'s queue.` : ""}`}
            >
              {task === null || task === undefined ? (
                <Alert color="danger" variant="flat">
                  {task === null
                    ? "This order has no packing task, so it cannot be packed. One opens when the order is approved."
                    : "Warehouse could not be read, so the lines to pack are not shown. Reload the page to try again."}
                </Alert>
              ) : (
                <div className="flex flex-col divide-y divide-dashed divide-muted">
                  {task.lines.map((line) => {
                    const id = line.orderLineId;
                    const band = bands[id];
                    // A line not stocked by weight is measured in its stocking unit (pieces, feet), never weighed (FR-028).
                    const measure = line.stockedByWeight ? "kg" : (line.stockingUnitCode ?? line.unitCode);
                    // Only a line ordered in kg is weighed; any other packs as ordered.
                    const asOrdered = !packedLess[id] && packedAsOrdered(line) !== null;
                    return (
                      <div key={id} className="flex flex-col gap-4 py-5 first:pt-0 last:pb-0">
                        <div className="flex items-center gap-3">
                          <Avatar name={line.skuName} color="secondary" rounded="md" size="md" />
                          <div className="min-w-0 flex-1">
                            <Text className="font-medium text-gray-900">{line.skuName}</Text>
                            <Text className="text-sm text-gray-500">
                              Ordered {line.orderedQty} {line.unitCode}
                              {line.expectedWeight !== null ? ` · expected ${line.expectedWeight} ${measure}` : ""}
                            </Text>
                          </div>
                          {live.has(id) ? (
                            <Badge variant="flat" color="success" rounded="pill" className="gap-1">
                              <Check aria-hidden className="size-3.5" /> Recorded
                            </Badge>
                          ) : band === "amber" || band === "red" ? (
                            <Badge variant="flat" color={band === "red" ? "danger" : "warning"} rounded="pill">
                              {band === "red" ? "Red" : "Amber"}
                            </Badge>
                          ) : null}
                        </div>
                        {live.has(id) ? null : (
                          <div className="grid grid-cols-1 items-start gap-4 @xl:grid-cols-2 @4xl:grid-cols-4">
                            {asOrdered ? (
                              <div className="col-span-full flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg bg-gray-50 px-4 py-3">
                                <Text className="text-sm text-gray-600">
                                  Packs as ordered:{" "}
                                  <span className="font-medium text-gray-900">
                                    {line.orderedQty} {line.unitCode}
                                  </span>
                                  . Nothing to enter: it was not ordered in kg.
                                </Text>
                                <Button type="button" variant="text" size="sm" className="h-auto p-0" onClick={() => setPackedLess((was) => ({ ...was, [id]: true }))}>
                                  Packed less?
                                </Button>
                              </div>
                            ) : line.isCountBased ? (
                              <>
                                <Field label="Pieces" name={`pieces:${id}`} type="number" min="1" step="1" required />
                                {/* A disabled field is not sent, so a ticked Standard size never travels with a length. */}
                                <Field
                                  key={`length:${id}:${standard[id] ? "standard" : "cut"}`}
                                  label="Cut length"
                                  name={`length:${id}`}
                                  type="number"
                                  min="0"
                                  step="any"
                                  disabled={standard[id] === true}
                                  placeholder={standard[id] ? "Standard size" : undefined}
                                  hint={standard[id] ? "Not needed — Standard size is ticked." : "Or tick Standard size."}
                                />
                                {line.weightCrossCheckRequired ? (
                                  <Field label="Total weight (kg)" name={`weight:${id}`} type="number" min="0" step="any" required hint="The scale reading for all the pieces." />
                                ) : null}
                                <Checkbox
                                  label="Standard size"
                                  name={`standard:${id}`}
                                  checked={standard[id] === true}
                                  onChange={(event) => setStandard((was) => ({ ...was, [id]: event.target.checked }))}
                                  className="@xl:mt-9"
                                />
                              </>
                            ) : (
                              <Field
                                label={line.stockedByWeight ? "Packed weight (kg)" : `Packed quantity (${measure})`}
                                name={`weight:${id}`}
                                type="number"
                                min="0"
                                step="any"
                                required
                              />
                            )}
                            {band === "amber" ? (
                              <>
                                <SelectField label="Reason" name={`reason:${id}`} defaultValue="" required>
                                  <option value="">Choose a reason…</option>
                                  {REASON_CODES.map((code) => (
                                    <option key={code} value={code}>
                                      {reasonCodeLabel(code)}
                                    </option>
                                  ))}
                                </SelectField>
                                <Field label="Reason note" name={`note:${id}`} hint="Required for Other." wrapperClassName="@4xl:col-span-3" />
                              </>
                            ) : null}
                            {band === "red" ? (
                              <Field label="Override reason" name={`override:${id}`} required wrapperClassName="col-span-full" hint="Recorded with your name as the supervisor's override." />
                            ) : null}
                          </div>
                        )}
                        {refusals[id] ? (
                          <Alert color={band === "red" ? "danger" : "warning"} variant="flat" icon={<Warning aria-hidden className="size-4" />}>
                            {refusals[id]}
                          </Alert>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </StepCard>
          ) : null}

          {shortPack || order.shortFulfilmentPending ? (
            <StepCard
              status="stopped"
              icon={<ShortIcon aria-hidden className="size-5" />}
              title="Short pack"
              description="Warehouse packed less than was ordered. Shipping short invoices what was packed; to hold the order instead, go back and use Settle short fulfilment."
            >
              <Field label="Why ship it short?" name="shipShortReason" required />
            </StepCard>
          ) : null}

          {!order.invoiceId && target !== "packed" ? (
            <StepCard
              number={numberOf("invoiced")}
              status={statusOf("invoiced")}
              icon={<InvoiceIcon aria-hidden className="size-5" />}
              title="Invoice"
              description="Nothing to enter. Billing raises it from the pack and it is finalised here."
            >
              <ul className="flex flex-col gap-2.5 text-sm text-gray-600">
                {[
                  "Raised on the packed quantities, not the ordered ones.",
                  "Registered for an IRN where e-invoicing applies.",
                  "Queued for Tally and posted to the customer's ledger.",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-2.5">
                    <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-green-dark" />
                    {point}
                  </li>
                ))}
              </ul>
              {invoiceLate ? (
                <Alert color="warning" variant="flat" className="mt-4">
                  Billing has not raised the invoice yet. Submit again in a minute — the pack is already recorded.
                </Alert>
              ) : null}
            </StepCard>
          ) : null}

          {showDispatch ? (
            <StepCard
              number={numberOf("dispatched")}
              status={statusOf("dispatched")}
              icon={<TruckIcon aria-hidden className="size-5" />}
              title="Dispatch"
              description={invoiced ? "How it leaves the warehouse." : "Taken only once the invoice is finalised — the run waits for it, then dispatches with these details."}
            >
              <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @xl:gap-4">
                {METHODS.map((method) => (
                  <AdvancedRadio
                    key={method.value}
                    name="deliveryType"
                    value={method.value}
                    checked={deliveryType === method.value}
                    onChange={() => setDeliveryType(method.value)}
                    className="[&_.rizzui-advanced-checkbox]:!px-5 [&_.rizzui-advanced-checkbox]:!py-4"
                    inputClassName="[&~span]:border-0 [&~span]:ring-1 [&~span]:ring-gray-200 [&~span:hover]:ring-primary [&:checked~span:hover]:ring-primary [&:checked~span]:border-1 [&:checked~.rizzui-advanced-checkbox]:ring-2 [&~span>.icon]:opacity-0 [&:checked~span>.icon]:opacity-100"
                  >
                    <span className="flex items-start gap-3 pe-8">
                      <span className="mt-0.5 text-primary">{method.icon}</span>
                      <span className="block">
                        <Title as="h6" className="mb-1 block font-lexend text-sm font-medium">
                          {method.title}
                        </Title>
                        <Text as="span" className="block text-sm font-normal leading-relaxed text-gray-500">
                          {method.text}
                        </Text>
                      </span>
                    </span>
                    <Chosen aria-hidden className="icon absolute end-4 top-4 size-5 text-primary duration-300" />
                  </AdvancedRadio>
                ))}
              </div>
              {deliveryType === "in_house" ? (
                <>
                  <div className="mt-5 grid grid-cols-1 gap-4 border-t border-muted pt-5 @xl:grid-cols-2 @4xl:grid-cols-3">
                    {dispatchers ? (
                      <SelectField
                        key={dispatch?.assignedDispatcher?.id ?? "none"}
                        label="Dispatcher"
                        name="dispatcherId"
                        defaultValue={dispatch?.assignedDispatcher?.id ?? ""}
                        required
                        disabled={dispatchers.length === 0}
                        disabledReason={dispatchers.length === 0 ? "No active account may dispatch." : undefined}
                        hint="Takes it out and records the delivery from their app."
                      >
                        <option value="">{dispatchers.length === 0 ? "No one to assign" : "Choose who takes it out…"}</option>
                        {dispatchers.map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.displayName ?? person.email ?? "Unnamed account"}
                          </option>
                        ))}
                      </SelectField>
                    ) : (
                      <Alert color="warning" variant="flat">
                        The list of people who may dispatch could not be read. It still dispatches, with no one assigned.
                      </Alert>
                    )}
                  </div>
                  <div className="mt-5 flex flex-col gap-4 border-t border-muted pt-5">
                    <Text className="text-sm font-medium text-gray-900">Transport</Text>
                    <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2 @4xl:grid-cols-3">
                      {TRANSPORT.map((field) => (
                        <Field
                          key={field.name}
                          label={field.label}
                          name={field.name}
                          type={field.type ?? "text"}
                          required={field.always}
                          hint={field.name === "transporterName" ? "Your own company, for your own vehicle." : undefined}
                        />
                      ))}
                      {/* Billing sets both pincodes itself and ignores what is sent (owner decision, 2026-10-26). */}
                      <Field
                        label="From pincode"
                        name="fromPincode"
                        readOnly
                        className="bg-gray-100"
                        defaultValue={dispatch?.fromPincode ?? ""}
                        placeholder="Set by Billing"
                        hint="Your registered place of business. Set by Billing."
                      />
                      <Field
                        label="To pincode"
                        name="toPincode"
                        readOnly
                        className="bg-gray-100"
                        defaultValue={dispatch?.shippingPincode ?? ""}
                        placeholder="Set by Billing"
                        hint="The order's delivery address, the one its delivery charge was priced on."
                      />
                      <SelectField label="Mode of transport" name="modeOfTransport" defaultValue="road" required>
                        {TRANSPORT_MODES.map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </SelectField>
                    </div>
                  </div>
                </>
              ) : null}
            </StepCard>
          ) : null}

          {showDelivery ? (
            <StepCard
              number={numberOf("delivered")}
              status={statusOf("delivered")}
              icon={<SignatureIcon aria-hidden className="size-5" />}
              title="Delivery"
              description={deliveryType === "third_party" ? "The transporter's delivery is confirmed on submit — nothing to enter." : "Who received it and their signature."}
            >
              {deliveryType === "in_house" ? (
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 items-start gap-4 @xl:grid-cols-2">
                    <Field label="Received by" name="receiverName" required maxLength={200} />
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between gap-3">
                        <Text className="text-sm font-medium">
                          Signature <span className="text-red">*</span>
                        </Text>
                        <div role="radiogroup" aria-label="How the signature is taken" className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-medium">
                          {(["draw", "upload"] as const).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              role="radio"
                              aria-checked={signMode === mode}
                              onClick={() => {
                                setSignMode(mode);
                                setSignature(null);
                              }}
                              className={cn(
                                "rounded-md px-3 py-1 transition-colors",
                                signMode === mode ? "bg-gray-0 text-gray-900 shadow-sm dark:bg-gray-50" : "text-gray-500 hover:text-gray-800",
                              )}
                            >
                              {mode === "draw" ? "Draw" : "Upload photo"}
                            </button>
                          ))}
                        </div>
                      </div>
                      {signMode === "draw" ? (
                        <SignaturePad onChange={(data) => setSignature(data ? { name: "Drawn on screen", data } : null)} />
                      ) : signature ? (
                        <div className="flex items-center gap-3 rounded-lg border border-muted p-2.5">
                          <img src={signature.data} alt="Signed invoice or challan" className="h-14 w-20 rounded border border-muted bg-white object-contain" />
                          <Text className="min-w-0 flex-1 truncate text-sm">{signature.name}</Text>
                          <Button type="button" variant="text" size="sm" onClick={() => setSignature(null)}>
                            Change
                          </Button>
                        </div>
                      ) : (
                        <Upload
                          accept="img"
                          multiple={false}
                          aria-label="Photo of the signed invoice or challan"
                          placeholderText={<UploadDropHint />}
                          iconClassName="size-8 shrink-0"
                          className="p-4"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) void readImage(file).then((data) => setSignature({ name: file.name, data }));
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 border-t border-muted pt-5">
                    <Text className="text-sm font-medium">Delivered short? Only for a line that was.</Text>
                    {order.lines.map((line) => (
                      <div key={line.lineId} className="grid grid-cols-1 items-start gap-4 @xl:grid-cols-[minmax(0,1fr)_10rem_minmax(0,1fr)]">
                        <Text className="pt-2 text-sm text-gray-700 @xl:pt-9">{line.skuName}</Text>
                        <Field label="Delivered" name={`delivered:${line.lineId}`} type="number" min="0" step="any" hint={`${line.unitCode}, blank for all`} />
                        <Field label="Why short" name={`shortage:${line.lineId}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </StepCard>
          ) : null}
        </fieldset>

        <aside className="sticky top-24 mt-6 flex flex-col gap-5 @5xl:col-span-4 @5xl:mt-0">
          <section className="rounded-xl border border-muted bg-gray-0 p-5 dark:bg-gray-50">
            <Title as="h4" className="font-semibold">
              Order
            </Title>
            <dl className="mt-4 flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-gray-500">Number</dt>
                <dd>
                  <Link to={orderPath(order.orderNumber)} className="font-medium text-gray-900 hover:underline">
                    {order.orderNumber}
                  </Link>
                </dd>
              </div>
              {order.customerName ? (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-gray-500">Customer</dt>
                  <dd className="truncate font-medium text-gray-900">{order.customerName}</dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-3">
                <dt className="text-gray-500">Payment</dt>
                <dd className="font-medium text-gray-900">{order.orderType === "credit" ? "On credit · B2B" : "Prepaid · B2C"}</dd>
              </div>
            </dl>
            <ul className="mt-4 flex flex-col gap-2 border-t border-muted pt-4 text-sm">
              {order.lines.map((line) => (
                <li key={line.lineId} className="flex items-start justify-between gap-3">
                  <span className="min-w-0 text-gray-700">{line.skuName}</span>
                  <span className="shrink-0 tabular-nums text-gray-500">
                    {line.orderedQuantity} {line.unitCode}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center justify-between border-t border-muted pt-4 text-base font-bold text-gray-1000">
              Order total
              <span className="tabular-nums">{formatMoney(money(order.grandTotal))}</span>
            </div>
            <Text className="mt-1 text-xs text-gray-500">As ordered. The invoice follows the packed quantities.</Text>
          </section>

          <section className="rounded-xl border border-muted bg-gray-0 p-5 dark:bg-gray-50">
            <Title as="h4" className="font-semibold">
              Steps
            </Title>
            <StepTracker plan={plan} statusOf={statusOf} className="mt-4" />

            <Button type="submit" name="intent" value="all" size="lg" isLoading={busy} disabled={!runnable} className="mt-6 w-full">
              Complete {left.length} {left.length === 1 ? "step" : "steps"}
            </Button>
            {canStopAtInvoice ? (
              // No browser validation: the dispatch fields are not needed to stop at the invoice, and
              // Warehouse refuses a missing reading by line itself.
              <Button type="submit" name="intent" value="pack" variant="outline" formNoValidate disabled={busy || !runnable} className="mt-2 w-full">
                {packed ? "Finalise the invoice only" : "Save packing only"}
              </Button>
            ) : null}
            <Button type="button" variant="text" className="mt-2 w-full" disabled={busy} onClick={goBack}>
              Cancel
            </Button>
            <Text className="mt-3 text-xs leading-relaxed text-gray-500">
              {invoiced || !showDispatch
                ? "If a step is refused, the ones before it stay done and what you typed stays here. Submit again to carry on."
                : "Nothing is dispatched before Billing raises the invoice and it is finalised; the run waits for it. Save packing only stops there."}
            </Text>
          </section>
        </aside>
      </form>

      <CompleteProgress
        open={progress !== "closed"}
        phase={progress === "closed" ? "running" : progress}
        orderNumber={order.orderNumber}
        from={from}
        to={target}
        plan={plan}
        statusOf={statusOf}
        running={running}
        message={
          progress === "invoiced"
            ? `The pack is recorded and ${order.invoiceNumber ? `invoice ${order.invoiceNumber}` : "the invoice"} is finalised. Dispatch is next.`
            : progress === "stopped"
              ? "The pack is recorded, but Billing has not raised the invoice, so nothing was dispatched. Everything you entered is kept — submit again in a minute to carry on."
              : (WAITING[running ?? ""] ?? "Starting…")
        }
        invoiceNumber={order.invoiceNumber}
        continueLoading={revalidator.state === "loading"}
        onContinue={() => setProgress("closed")}
        onClose={() => setProgress("closed")}
      />
    </div>
  );
}

/** One step's card: its number (a tick once done), title and line of description, its fields under a rule. */
function StepCard({
  number,
  status,
  icon,
  title,
  description,
  children,
}: {
  number?: number;
  status: StepStatus;
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border bg-gray-0 dark:bg-gray-50",
        status === "stopped" ? "border-red/40" : status === "running" ? "border-primary/50" : "border-muted",
      )}
    >
      <header className="flex items-start gap-4 p-5 @xl:px-6">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-lighter text-primary-dark">{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Title as="h4" className="text-base font-semibold">
              {number ? <span className="text-gray-400">{number}. </span> : null}
              {title}
            </Title>
            {status === "running" ? (
              <Badge variant="flat" rounded="pill" className="gap-1.5">
                <Loader size="sm" /> In progress
              </Badge>
            ) : status === "stopped" ? (
              <Badge variant="flat" color="danger" rounded="pill">
                Needs your attention
              </Badge>
            ) : status === "done" ? (
              <Badge variant="flat" color="success" rounded="pill">
                Done
              </Badge>
            ) : null}
          </div>
          <Text className="mt-1 text-sm text-gray-500">{description}</Text>
        </div>
      </header>
      {children ? <div className="border-t border-muted p-5 @xl:px-6">{children}</div> : null}
    </section>
  );
}

/** The steps as a vertical tracker: a number, a spinner while running, a tick once done, a warning where a run stopped. */
function StepTracker({ plan, statusOf, className }: { plan: string[]; statusOf: (step: string) => StepStatus; className?: string }) {
  return (
    <ol className={cn("flex flex-col", className)}>
      {plan.map((step, index) => {
        const status = statusOf(step);
        return (
          <li key={step} className="relative flex gap-3 pb-5 last:pb-0 before:absolute before:start-[15px] before:top-9 before:h-[calc(100%-2.75rem)] before:w-px before:bg-gray-200 last:before:hidden">
            <span
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                status === "done" && "bg-green text-white",
                status === "running" && "bg-primary text-white",
                status === "stopped" && "bg-red text-white",
                status === "todo" && "border border-gray-300 text-gray-500",
              )}
            >
              {status === "done" ? <Check aria-hidden className="size-4" /> : status === "running" ? <Loader size="sm" /> : status === "stopped" ? <Warning aria-hidden className="size-4" /> : index + 1}
            </span>
            <div className="min-w-0 pt-1">
              <Text className={cn("text-sm font-medium", status === "todo" ? "text-gray-700" : "text-gray-900")}>{stateLabel(step)}</Text>
              <Text className={cn("text-xs", status === "stopped" ? "text-red-dark" : "text-gray-500")}>
                {status === "done" ? "Done" : status === "running" ? `${RUNNING[step] ?? "Working"}…` : status === "stopped" ? "Needs your attention" : "On submit"}
              </Text>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The receiver signs inside the box with a finger, stylus or mouse. Each finished stroke hands the
 * drawing up as a PNG data URI — what Ordering's deliver takes as the signature — and Clear hands up
 * null. The canvas is drawn at a fixed resolution and scaled by CSS, so pointer positions are mapped
 * into it.
 */
function SignaturePad({ onChange }: { onChange: (data: string | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [signed, setSigned] = useState(false);

  // A white ground, so the PNG reads as ink on paper wherever it is shown.
  const blank = () => {
    const ctx = canvas.current?.getContext("2d");
    if (!ctx || !canvas.current) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.current.width, canvas.current.height);
  };
  useEffect(blank, []);

  const at = (event: PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - box.left) * event.currentTarget.width) / box.width,
      y: ((event.clientY - box.top) * event.currentTarget.height) / box.height,
    };
  };

  return (
    <div className="flex flex-col gap-1.5">
      <canvas
        ref={canvas}
        width={640}
        height={200}
        aria-label="Signature box: sign here"
        className={cn("h-36 w-full cursor-crosshair touch-none rounded-lg border-2 border-dashed bg-white", signed ? "border-primary/40" : "border-gray-300")}
        onPointerDown={(event) => {
          const ctx = event.currentTarget.getContext("2d");
          if (!ctx) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          drawing.current = true;
          const { x, y } = at(event);
          ctx.strokeStyle = "#0f172a";
          ctx.lineWidth = 3;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.beginPath();
          ctx.moveTo(x, y);
        }}
        onPointerMove={(event) => {
          const ctx = event.currentTarget.getContext("2d");
          if (!drawing.current || !ctx) return;
          const { x, y } = at(event);
          ctx.lineTo(x, y);
          ctx.stroke();
        }}
        onPointerUp={(event) => {
          if (!drawing.current) return;
          drawing.current = false;
          setSigned(true);
          onChange(event.currentTarget.toDataURL("image/png"));
        }}
      />
      <div className="flex items-center justify-between">
        <Text className="text-xs text-gray-500">{signed ? "Signed. Clear to sign again." : "The receiver signs inside the box."}</Text>
        <Button
          type="button"
          variant="text"
          size="sm"
          disabled={!signed}
          onClick={() => {
            blank();
            setSigned(false);
            onChange(null);
          }}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
